import { useState, useEffect } from "react";
import { jobsAPI } from "@/lib/api";
import { useTranslation, EUROPEAN_COUNTRIES, POSITIONS_LIST, COUNTRY_FLAGS } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import {
  Plus, Pencil, Trash2, XCircle, CheckCircle2, Briefcase, MapPin, Clock, ArrowLeft, Loader2, Eye
} from "lucide-react";
import { toast } from "sonner";
import { trackEvent } from "@/lib/analytics";

const EMPLOYMENT_TYPES = ["full_time", "part_time", "temporary", "seasonal", "internship"];
const EMPLOYMENT_LABELS = {
  full_time: "Full time", part_time: "Part time", temporary: "Temporary",
  seasonal: "Seasonal", internship: "Internship",
};

const EMPTY_FORM = {
  title: "", position: "", description: "", country: "", region: "", city: "",
  employment_type: "full_time", salary_min: "", salary_max: "",
  requirements: "", experience_years_min: "", languages_required: [],
  accommodation_provided: false, expires_at: "",
};

export function JobPostingsView({ hotel, t, tc, tp }) {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("list"); // list | form
  const [editingJob, setEditingJob] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  useEffect(() => { loadJobs(); }, []);

  const loadJobs = async () => {
    try {
      const res = await jobsAPI.getMyJobs();
      setJobs(res.data);
    } catch {} finally { setLoading(false); }
  };

  const handleNew = () => {
    setEditingJob(null);
    setForm({ ...EMPTY_FORM, country: hotel?.country || "" });
    setView("form");
  };

  const handleEdit = (job) => {
    setEditingJob(job);
    setForm({
      title: job.title || "",
      position: job.position || "",
      description: job.description || "",
      country: job.country || "",
      region: job.region || "",
      city: job.city || "",
      employment_type: job.employment_type || "full_time",
      salary_min: job.salary_min || "",
      salary_max: job.salary_max || "",
      requirements: job.requirements || "",
      experience_years_min: job.experience_years_min || "",
      languages_required: job.languages_required || [],
      accommodation_provided: job.accommodation_provided || false,
      expires_at: job.expires_at ? job.expires_at.split("T")[0] : "",
    });
    setView("form");
  };

  const handleSave = async () => {
    if (!form.title || !form.position || !form.description || !form.country) {
      toast.error("Title, position, description and country are required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        salary_min: form.salary_min ? parseFloat(form.salary_min) : null,
        salary_max: form.salary_max ? parseFloat(form.salary_max) : null,
        experience_years_min: form.experience_years_min ? parseInt(form.experience_years_min) : null,
        expires_at: form.expires_at ? `${form.expires_at}T23:59:59Z` : null,
        accommodation_provided: form.accommodation_provided || null,
      };
      if (editingJob) {
        await jobsAPI.update(editingJob.job_id, payload);
        toast.success("Job updated");
      } else {
        await jobsAPI.create(payload);
        trackEvent("hotel_job_published");
        toast.success("Job published!");
      }
      await loadJobs();
      setView("list");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error saving job");
    } finally { setSaving(false); }
  };

  const handleClose = async (jobId) => {
    try {
      await jobsAPI.close(jobId);
      toast.success("Job closed");
      loadJobs();
    } catch { toast.error("Error"); }
  };

  const handleDelete = async (jobId) => {
    try {
      await jobsAPI.remove(jobId);
      setDeleteTarget(null);
      toast.success("Job deleted");
      loadJobs();
    } catch { toast.error("Error"); }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-gold-500" /></div>;
  }

  // FORM VIEW
  if (view === "form") {
    return (
      <div className="space-y-6" data-testid="job-form">
        <div className="flex items-center gap-3">
          <button onClick={() => setView("list")} className="text-slate-500 hover:text-slate-700">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h2 className="font-sans font-bold text-xl text-slate-900">
            {editingJob ? "Edit Job" : "Publish New Job"}
          </h2>
        </div>

        <Card className="border-slate-200 bg-white shadow-sm">
          <CardContent className="p-5 space-y-4">
            <div>
              <Label className="font-body text-sm">Job Title *</Label>
              <Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                placeholder="e.g. Receptionist Summer Season 2026" className="h-11 font-body mt-1" data-testid="job-title-input" />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="font-body text-sm">Position *</Label>
                <Select value={form.position} onValueChange={v => setForm(f => ({ ...f, position: v }))}>
                  <SelectTrigger className="h-11 font-body mt-1" data-testid="job-position-select"><SelectValue placeholder="Select position" /></SelectTrigger>
                  <SelectContent>
                    {POSITIONS_LIST.map(p => <SelectItem key={p} value={p}>{tp(p)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="font-body text-sm">Employment Type</Label>
                <Select value={form.employment_type} onValueChange={v => setForm(f => ({ ...f, employment_type: v }))}>
                  <SelectTrigger className="h-11 font-body mt-1" data-testid="job-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {EMPLOYMENT_TYPES.map(t => <SelectItem key={t} value={t}>{EMPLOYMENT_LABELS[t]}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label className="font-body text-sm">Description *</Label>
              <Textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                placeholder="Describe the role, responsibilities and what you offer..."
                className="font-body mt-1 min-h-[120px]" data-testid="job-desc-input" />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <Label className="font-body text-sm">Country *</Label>
                <Select value={form.country} onValueChange={v => setForm(f => ({ ...f, country: v }))}>
                  <SelectTrigger className="h-11 font-body mt-1" data-testid="job-country-select"><SelectValue placeholder="Country" /></SelectTrigger>
                  <SelectContent>
                    {EUROPEAN_COUNTRIES.map(c => <SelectItem key={c} value={c}>{COUNTRY_FLAGS[c]} {tc(c)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="font-body text-sm">Region</Label>
                <Input value={form.region} onChange={e => setForm(f => ({ ...f, region: e.target.value }))}
                  placeholder="e.g. Balearic Islands" className="h-11 font-body mt-1" />
              </div>
              <div>
                <Label className="font-body text-sm">City</Label>
                <Input value={form.city} onChange={e => setForm(f => ({ ...f, city: e.target.value }))}
                  placeholder="e.g. Palma de Mallorca" className="h-11 font-body mt-1" />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <Label className="font-body text-sm">Salary Min (EUR/month)</Label>
                <Input type="number" value={form.salary_min} onChange={e => setForm(f => ({ ...f, salary_min: e.target.value }))}
                  placeholder="Optional" className="h-11 font-body mt-1" />
              </div>
              <div>
                <Label className="font-body text-sm">Salary Max (EUR/month)</Label>
                <Input type="number" value={form.salary_max} onChange={e => setForm(f => ({ ...f, salary_max: e.target.value }))}
                  placeholder="Optional" className="h-11 font-body mt-1" />
              </div>
              <div>
                <Label className="font-body text-sm">Min. Experience (years)</Label>
                <Input type="number" value={form.experience_years_min} onChange={e => setForm(f => ({ ...f, experience_years_min: e.target.value }))}
                  placeholder="Optional" className="h-11 font-body mt-1" />
              </div>
            </div>

            <div>
              <Label className="font-body text-sm">Requirements</Label>
              <Textarea value={form.requirements} onChange={e => setForm(f => ({ ...f, requirements: e.target.value }))}
                placeholder="Languages, certifications, skills..." className="font-body mt-1 min-h-[80px]" />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="font-body text-sm">Expiry Date</Label>
                <Input type="date" value={form.expires_at} onChange={e => setForm(f => ({ ...f, expires_at: e.target.value }))}
                  min={new Date().toISOString().split("T")[0]} className="h-11 font-body mt-1" data-testid="job-expires-input" />
              </div>
              <div className="flex items-center gap-3 pt-6">
                <Switch checked={form.accommodation_provided}
                  onCheckedChange={v => setForm(f => ({ ...f, accommodation_provided: v }))} />
                <Label className="font-body text-sm">Accommodation provided</Label>
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              <Button className="bg-emerald-500 hover:bg-emerald-600 text-white font-semibold flex-1 h-11"
                onClick={handleSave} disabled={saving} data-testid="job-save-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                {editingJob ? "Update Job" : "Publish Job"}
              </Button>
              <Button variant="outline" className="h-11" onClick={() => setView("list")}>Cancel</Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // LIST VIEW
  return (
    <div className="space-y-4" data-testid="job-list">
      <div className="flex items-center justify-between">
        <h2 className="font-sans font-bold text-xl text-slate-900">My Job Postings</h2>
        <Button className="bg-emerald-500 hover:bg-emerald-600 text-white font-semibold" onClick={handleNew} data-testid="new-job-btn">
          <Plus className="w-4 h-4 mr-2" /> Publish Job
        </Button>
      </div>

      {jobs.length === 0 ? (
        <Card className="border-slate-200 bg-white">
          <CardContent className="py-12 text-center">
            <Briefcase className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-sm text-slate-500 font-body">No job postings yet. Publish your first job to start attracting candidates.</p>
          </CardContent>
        </Card>
      ) : (
        jobs.map(job => (
          <Card key={job.job_id} className="border-slate-200 bg-white shadow-sm" data-testid={`job-card-${job.job_id}`}>
            <CardContent className="p-5">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-sans font-semibold text-slate-900 truncate">{job.title}</h3>
                    <Badge className={`text-xs font-body ${job.status === "active" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-500 border-slate-200"}`}>
                      {job.status === "active" ? "Active" : "Closed"}
                    </Badge>
                  </div>
                  <div className="flex flex-wrap gap-3 text-xs text-slate-500 font-body">
                    <span className="flex items-center gap-1"><Briefcase className="w-3 h-3" />{tp(job.position)}</span>
                    <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{[job.city, job.region, tc(job.country)].filter(Boolean).join(", ")}</span>
                    <span>{EMPLOYMENT_LABELS[job.employment_type] || job.employment_type}</span>
                    {job.salary_min && <span>{job.salary_min}{job.salary_max ? `-${job.salary_max}` : ""} EUR</span>}
                    {job.expires_at && <span className="flex items-center gap-1"><Clock className="w-3 h-3" />Expires: {job.expires_at.split("T")[0]}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  {job.status === "active" && (
                    <a href={`/jobs/${job.slug}`} target="_blank" rel="noopener noreferrer"
                      className="p-1.5 rounded-md text-slate-400 hover:text-sky-500 hover:bg-sky-50 transition-colors">
                      <Eye className="w-4 h-4" />
                    </a>
                  )}
                  <button className="p-1.5 rounded-md text-slate-400 hover:text-gold-600 hover:bg-gold-50 transition-colors"
                    onClick={() => handleEdit(job)} data-testid={`edit-job-${job.job_id}`}>
                    <Pencil className="w-4 h-4" />
                  </button>
                  {job.status === "active" && (
                    <button className="p-1.5 rounded-md text-slate-400 hover:text-amber-500 hover:bg-amber-50 transition-colors"
                      onClick={() => handleClose(job.job_id)}>
                      <XCircle className="w-4 h-4" />
                    </button>
                  )}
                  <button className="p-1.5 rounded-md text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                    onClick={() => setDeleteTarget(job)} data-testid={`delete-job-${job.job_id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))
      )}

      <Dialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <DialogContent className="sm:max-w-sm p-0 overflow-hidden border-0 rounded-lg" data-testid="delete-job-dialog">
          <DialogTitle className="sr-only">Delete job</DialogTitle>
          <div className="px-6 pt-6 pb-4 text-center">
            <Trash2 className="w-10 h-10 text-red-500 mx-auto mb-3" />
            <h3 className="font-sans font-bold text-lg text-slate-900 mb-1">Delete this job?</h3>
            <p className="text-sm text-slate-500 font-body">This will permanently remove the job posting and its public page.</p>
          </div>
          <div className="px-6 pb-5 flex gap-3">
            <Button variant="outline" className="flex-1" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button className="flex-1 bg-red-500 hover:bg-red-600 text-white" onClick={() => handleDelete(deleteTarget?.job_id)} data-testid="confirm-delete-job">
              Delete
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
